import type {
	JiraSpaRoute,
	RouterContext,
} from '@atlassian/react-resource-router/src/common/types.tsx';
import { getProjectsSidebarCurrentUrl } from '@jira/navigation-apps__routing-hooks/src/getProjectsSidebarCurrentUrl.tsx';

/**
 * Last project-identifying URL seen in this browser session, keyed by site.
 *
 * Only read and written in the browser. One Node process serves many requests from
 * the same module instance, so a value retained during server rendering would be
 * visible to the next tenant's request.
 */
const lastProjectUrlByCloudId = new Map<string, string>();

/**
 * The `currentURL` for `jira_projectsSidebarMenu`, reduced to the current project and
 * retained across navigations that leave one.
 *
 * `currentURL` is a variable on the app-chrome query, so it forms part of that query's
 * cache key. Letting it clear on Plans or Your Work would re-key the query and refetch
 * the whole chrome, even though the projects menu should still be showing the project
 * the user came from. Retaining the last value keeps the key stable until the route
 * names a different project.
 */
export const getStickyProjectsSidebarCurrentUrl = (
	routerContext: RouterContext<JiraSpaRoute>,
	cloudId: string,
): string | undefined => {
	const currentURL = getProjectsSidebarCurrentUrl(routerContext.match, routerContext.query);

	if (typeof window === 'undefined') {
		return currentURL;
	}

	if (currentURL !== undefined) {
		lastProjectUrlByCloudId.set(cloudId, currentURL);
		return currentURL;
	}

	return lastProjectUrlByCloudId.get(cloudId);
};

/** @internal test-only */
export const resetStickyProjectsSidebarCurrentUrl = (): void => {
	lastProjectUrlByCloudId.clear();
};
